import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isPlatformStaffOrPending } from '../../lib/permissions';
import { getErrorCode, getErrorMessage, rateLimitMessage, type TLike } from '../../lib/apiError';
import { getPlatformMfaToken, MFA_FORBIDDEN_EVENT, MFA_REQUIRED_EVENT } from '../../lib/platformMfaToken';
import {
  confirmEnrollment, formatRecoveryCode, getMfaStatus, RECOVERY_CODE_RE, redeemRecoveryCode, startEnrollment, TOTP_RE, verifyMfa,
  refreshMeWithMfa, type EnrollStart,
} from '../../lib/platformMfa';
import { TotpQr } from './TotpQr';

type T = (k: string, o?: Record<string, unknown>) => string;

/** Texto de error via normalizador: 429 -> rateLimitMessage (retry_after hasta 15 min); resto -> catalogo por codigo. */
export function mfaErrorText(err: unknown, t: T, fallbackKey = 'platform_mfa.error_generic'): string {
  const tl = t as unknown as TLike;
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 429) return rateLimitMessage(err, tl);
  return getErrorMessage(err, t(fallbackKey, { defaultValue: 'No pudimos completar la verificación. Intenta de nuevo.' }), tl);
}

/** Secreto base32 del otpauth:// para captura manual (solo en pantalla, nunca en logs). */
export function secretFromUri(uri: string): string {
  try { return new URL(uri).searchParams.get('secret') ?? ''; } catch { return ''; }
}

const card = 'max-w-md mx-auto mt-10 p-6 bg-surface-raised border border-border-default rounded-[var(--c-card-radius)] space-y-4';
const input = 'w-full px-3 py-2 border border-border-default rounded-md font-mono tracking-widest';
const btn = 'px-4 py-2 rounded-md bg-brand-primary text-white disabled:opacity-50';
const link = 'text-sm underline text-brand-primary';

function ErrorText({ msg }: { msg: string }) {
  return msg ? <p role="alert" className="text-sm text-status-danger">{msg}</p> : null;
}

/** Codigos de recuperacion: se muestran una sola vez y hay que confirmar que se guardaron. */
export function RecoveryCodesOnce({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  return (
    <div className="space-y-3" data-test="mfa-recovery-codes">
      <h2 className="text-lg font-semibold">{t('platform_mfa.codes_title', { defaultValue: 'Guarda tus códigos de recuperación' })}</h2>
      <p className="text-sm">{t('platform_mfa.codes_help', { defaultValue: 'Cada código sirve una sola vez si pierdes tu teléfono. No los volveremos a mostrar.' })}</p>
      <ul className="grid grid-cols-1 gap-1 font-mono text-sm">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        {t('platform_mfa.codes_saved', { defaultValue: 'Ya guardé mis códigos en un lugar seguro' })}
      </label>
      <button type="button" className={btn} disabled={!saved} onClick={onDone}>{t('platform_mfa.continue', { defaultValue: 'Continuar' })}</button>
    </div>
  );
}

function Enroll({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [start, setStart] = useState<EnrollStart | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState('');
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true); setErr(''); setExpired(false); setCode('');
    try { setStart(await startEnrollment()); } catch (e) { setErr(mfaErrorText(e, t)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!start) return;
    const ms = Date.parse(start.expires_at) - Date.now();
    if (!Number.isFinite(ms)) return;
    const id = window.setTimeout(() => setExpired(true), Math.max(0, ms));
    return () => window.clearTimeout(id);
  }, [start]);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!TOTP_RE.test(code)) { setErr(t('platform_mfa.code_format', { defaultValue: 'Escribe los 6 dígitos de tu app.' })); return; }
    setBusy(true); setErr('');
    try {
      const r = await confirmEnrollment(code);
      setStart(null);
      setCodes(r.recovery_codes);
    } catch (x) {
      if (getErrorCode(x) === 'MFA_ENROLLMENT_EXPIRED') setExpired(true);
      setErr(mfaErrorText(x, t));
    } finally { setBusy(false); }
  };

  if (codes) return <div className={card}><RecoveryCodesOnce codes={codes} onDone={onDone} /></div>;
  const secret = start ? secretFromUri(start.otpauth_uri) : '';
  return (
    <div className={card} data-test="mfa-enroll">
      <h1 className="text-xl font-semibold">{t('platform_mfa.enroll_title', { defaultValue: 'Activa la verificación en dos pasos' })}</h1>
      <p className="text-sm">{t('platform_mfa.enroll_help', { defaultValue: 'Las herramientas de plataforma requieren una app de autenticación (Google Authenticator, 1Password, Authy…).' })}</p>
      {!start ? (
        <>
          <ErrorText msg={err} />
          <button type="button" className={btn} disabled={busy} onClick={begin}>{t('platform_mfa.generate_qr', { defaultValue: 'Generar código QR' })}</button>
        </>
      ) : (
        <form onSubmit={confirm} className="space-y-3" noValidate>
          {expired ? (
            <p className="text-sm">{t('platform_mfa.qr_expired', { defaultValue: 'Este código QR venció. Genera uno nuevo para continuar.' })}</p>
          ) : (
            <>
              <TotpQr uri={start.otpauth_uri} label={t('platform_mfa.qr_alt', { defaultValue: 'Código QR para tu app de autenticación' })} />
              <p className="text-xs">{t('platform_mfa.qr_once', { defaultValue: 'El QR vence en 10 minutos. Si generas otro, este deja de servir.' })}</p>
              {secret && (
                <details className="text-xs">
                  <summary>{t('platform_mfa.manual_key', { defaultValue: '¿No puedes escanear? Clave manual' })}</summary>
                  <code className="break-all">{secret}</code>
                </details>
              )}
              <label className="block text-sm" htmlFor="mfa-enroll-code">{t('platform_mfa.code_label', { defaultValue: 'Código de 6 dígitos' })}</label>
              <input id="mfa-enroll-code" className={input} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} aria-invalid={!!err} />
            </>
          )}
          <ErrorText msg={err} />
          {expired ? (
            <button type="button" className={btn} disabled={busy} onClick={begin}>{t('platform_mfa.restart', { defaultValue: 'Generar un QR nuevo' })}</button>
          ) : (
            <button type="submit" className={btn} disabled={busy}>{t('platform_mfa.activate', { defaultValue: 'Activar' })}</button>
          )}
        </form>
      )}
    </div>
  );
}

function Verify({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = recovery ? RECOVERY_CODE_RE.test(formatRecoveryCode(code)) : TOTP_RE.test(code);
    if (!ok) {
      setErr(recovery ? t('platform_mfa.recovery_format', { defaultValue: 'El código de recuperación tiene el formato XXXXX-XXXXX-XXXXX-XXXXX.' })
        : t('platform_mfa.code_format', { defaultValue: 'Escribe los 6 dígitos de tu app.' }));
      return;
    }
    setBusy(true); setErr('');
    try {
      if (recovery) {
        const r = await redeemRecoveryCode(code);
        setRemaining(r.recovery_codes_remaining);
      } else {
        await verifyMfa(code);
        onDone();
      }
    } catch (x) { setErr(mfaErrorText(x, t)); } finally { setBusy(false); }
  };

  if (remaining !== null) {
    return (
      <div className={card} data-test="mfa-recovery-used">
        <p className="text-sm">{t('platform_mfa.recovery_remaining', { count: remaining, defaultValue: 'Te quedan {{count}} códigos de recuperación.' })}</p>
        <button type="button" className={btn} onClick={onDone}>{t('platform_mfa.continue', { defaultValue: 'Continuar' })}</button>
      </div>
    );
  }
  const id = recovery ? 'mfa-recovery-code' : 'mfa-verify-code';
  return (
    <form className={card} onSubmit={submit} noValidate data-test="mfa-verify">
      <h1 className="text-xl font-semibold">{t('platform_mfa.verify_title', { defaultValue: 'Verificación en dos pasos' })}</h1>
      <label className="block text-sm" htmlFor={id}>
        {recovery ? t('platform_mfa.recovery_label', { defaultValue: 'Código de recuperación' }) : t('platform_mfa.code_label', { defaultValue: 'Código de 6 dígitos' })}
      </label>
      <input id={id} className={input} value={code} aria-invalid={!!err}
        {...(recovery
          ? { autoComplete: 'off', maxLength: 23, onChange: (e: React.ChangeEvent<HTMLInputElement>) => setCode(formatRecoveryCode(e.target.value)) }
          : { inputMode: 'numeric' as const, autoComplete: 'one-time-code', maxLength: 6, onChange: (e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6)) })} />
      <ErrorText msg={err} />
      <button type="submit" className={btn} disabled={busy}>{t('platform_mfa.verify', { defaultValue: 'Verificar' })}</button>
      <button type="button" className={link} onClick={() => { setRecovery(!recovery); setCode(''); setErr(''); }}>
        {recovery ? t('platform_mfa.use_app', { defaultValue: 'Usar mi app de autenticación' }) : t('platform_mfa.use_recovery', { defaultValue: 'Usar un código de recuperación' })}
      </button>
    </form>
  );
}

type View = 'loading' | 'ok' | 'enroll' | 'verify' | 'error';

/**
 * 2FA del staff de plataforma (Backend feat/platform-staff-2fa 4baf2ca8). Decide con /auth/me
 * platform_staff_pending_mfa + GET /api/platform/mfa/status (no con is_platform_staff). 403 MFA_REQUIRED en
 * cualquier llamada /api/platform/* => vuelve a verificar (evento de apiClient).
 * Staff = is_platform_staff || platform_staff_pending_mfa (sin X-MFA-Token, /me da is_platform_staff=false).
 * `force`: el llamador ya sabe por el servidor que hace falta (Mensajes: X-Support-MFA-Required / 403 MFA_REQUIRED);
 * no mira /me y decide enrolar/verificar solo con GET /api/platform/mfa/status.
 * Al verificar: refreshMeWithMfa (una llamada a /me con el token) para que /me quede con is_platform_staff=true.
 */
type AxiosLikeError = { response?: { status?: number }; code?: string };
const TRANSIENT_CODES = new Set(['ECONNABORTED', 'ERR_NETWORK', 'ERR_CANCELED']);
/** Solo 5xx o fallos de axios sin respuesta (timeout de 15 s, red, abortada). 4xx, TypeError u otros: no (P3 Seguridad). */
export function isTransientStatusError(e: unknown): boolean {
  const x = e as AxiosLikeError;
  const st = x?.response?.status;
  if (typeof st === 'number') return st >= 500;
  return typeof x?.code === 'string' && TRANSIENT_CODES.has(x.code);
}
/** Etiqueta corta del fallo (sin texto del servidor) para que QA/Backend sepan de donde vino: status HTTP o codigo axios. */
export function statusErrorKind(e: unknown): string {
  const x = e as AxiosLikeError;
  return x?.response?.status ? `http-${x.response.status}` : (x?.code || 'network');
}

export default function PlatformMfaGate({ children, force = false }: { children: React.ReactNode; force?: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const staff = force || isPlatformStaffOrPending(user);
  const pending = force ? undefined : user?.platform_staff_pending_mfa;
  // Quien no es staff (p. ej. admin de cuenta en /monitoring) ve la pantalla de una vez, sin "Comprobando".
  const [view, setView] = useState<View>(() => (staff ? 'loading' : 'ok'));
  const [err, setErr] = useState('');
  const [errKind, setErrKind] = useState('');
  // QA P2 (1/4 en /admin/logs): un /mfa/status viejo (efecto re-ejecutado, desmontado o superado por verificar)
  // podia terminar despues y pisar la vista con 'error'. Solo la ultima comprobacion escribe la vista.
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // askServer: tras un 403 generico no se confia en el token local; se pregunta /mfa/status.
  const viewRef = useRef(view);
  viewRef.current = view;
  const check = useCallback(async (askServerIn = false) => {
    // Solo es silencioso si la pantalla ya esta visible; si no, es una comprobacion normal.
    const askServer = askServerIn && viewRef.current === 'ok';
    const seq = ++seqRef.current;
    abortRef.current?.abort();
    if (!askServer && !askServerIn && pending === false && getPlatformMfaToken()) { setView('ok'); return; }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    // askServer es silencioso: no desmonta la pantalla (evita bucle 403 -> loading -> remonta -> 403); solo cambia
    // la vista si el servidor dice que falta enrolar o verificar.
    if (!askServer) { setView('loading'); setErr(''); setErrKind(''); }
    for (let attempt = 0; ; attempt++) {
      try {
        const s = await getMfaStatus(ctrl.signal);
        if (seq !== seqRef.current) return;
        if (askServer && s.mfa_valid) return; // 2FA vigente: el 403 es real y lo muestra la pantalla
        setView(s.mfa_valid ? 'ok' : s.enrolled ? 'verify' : 'enroll');
        return;
      } catch (e) {
        if (seq !== seqRef.current) return;
        // Fallo transitorio (sin respuesta, timeout, abortada o 5xx): un reintento automatico antes de mostrar error.
        // P3 Seguridad: abortada por nosotros (desmontar / nueva comprobacion) => no se reintenta.
        if (attempt === 0 && !ctrl.signal.aborted && isTransientStatusError(e)) continue;
        if (askServer) return; // sin cambio de vista: el 403 original sigue visible en la pantalla
        setErrKind(statusErrorKind(e)); setErr(mfaErrorText(e, t)); setView('error');
        return;
      }
    }
  }, [pending, t]);

  useEffect(() => {
    if (!staff) { seqRef.current++; setView('ok'); return; }
    void check();
    return () => { seqRef.current++; abortRef.current?.abort(); };
  }, [staff, check]);

  // 403 generico en una ruta 2FA (p. ej. /api/monitoring) con el Gate montado: revisar /mfa/status (enroll/verify).
  useEffect(() => {
    if (!staff || force) return;
    const onForbidden = () => { void check(true); };
    window.addEventListener(MFA_FORBIDDEN_EVENT, onForbidden);
    return () => window.removeEventListener(MFA_FORBIDDEN_EVENT, onForbidden);
  }, [staff, force, check]);

  useEffect(() => {
    const onRequired = () => { seqRef.current++; abortRef.current?.abort(); setView('verify'); };
    window.addEventListener(MFA_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(MFA_REQUIRED_EVENT, onRequired);
  }, []);

  if (view === 'ok') return <>{children}</>;
  const verified = () => { seqRef.current++; abortRef.current?.abort(); void refreshMeWithMfa(); setView('ok'); };
  if (view === 'enroll') return <Enroll onDone={verified} />;
  if (view === 'verify') return <Verify onDone={verified} />;
  if (view === 'error') {
    return (
      <div className={card} data-test="mfa-gate-error" data-error-kind={errKind}>
        <ErrorText msg={err} />
        <button type="button" className={btn} onClick={() => void check()}>{t('platform_mfa.retry', { defaultValue: 'Reintentar' })}</button>
      </div>
    );
  }
  return <div role="status" aria-busy="true" className={card}>{t('platform_mfa.loading', { defaultValue: 'Comprobando verificación…' })}</div>;
}